import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import type { SummitStatus } from '@/lib/founder/computeSummitStatus';

interface Props {
  status: SummitStatus;
  founderScore: number;
}

export default function SummitProgressCard({ status, founderScore }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const progressPct = Math.max(0, Math.min(1, 1 - (status.rank - 1) / Math.max(status.cutoffRank, 1)));

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: c.text }]}>SUMMIT QUALIFICATION</Text>
        <View style={[styles.badge, { backgroundColor: status.isQualified ? c.accent + '20' : c.surface, borderColor: status.isQualified ? c.accent : c.border }]}>
          <Text style={[styles.badgeText, { color: status.isQualified ? c.accent : c.textSecondary }]}>
            {status.isQualified ? '✓ QUALIFIED' : 'NOT YET'}
          </Text>
        </View>
      </View>

      <View style={styles.statsRow}>
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>#{status.rank}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Your Rank</Text>
        </View>
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>Top {status.cutoffRank}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Cut Line</Text>
        </View>
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>{Math.round(founderScore)}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Founder Score</Text>
        </View>
      </View>

      <View style={[styles.progressTrack, { backgroundColor: c.border }]}>
        <View style={[styles.progressFill, { width: `${progressPct * 100}%` as any, backgroundColor: status.isQualified ? c.accent : c.textSecondary }]} />
      </View>
      <Text style={[styles.progressLabel, { color: c.textTertiary }]}>
        {status.isQualified
          ? `You're in the top ${status.cutoffRank} — Summit qualified`
          : `Rank #${status.rank} — top 10% qualify`}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 4,
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 11,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 1.5,
  },
  badge: {
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 1,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  stat: {
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    fontSize: 20,
    fontFamily: 'Inter_700Bold',
  },
  statLabel: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    marginTop: 2,
  },
  progressTrack: {
    height: 3,
    borderRadius: 2,
    marginBottom: 6,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressLabel: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
  },
});
